// Theme Context - Comprehensive color themes for Cosmic Compass
import React, { createContext, useContext, useState, useEffect } from 'react';

export const THEMES = {
    cosmic: {
        name: 'Cosmic Purple',
        id: 'cosmic',
        description: 'Classic mystical purple with gold accents',
        css: {
            // Primary palette
            '--theme-primary': '#9d4edd',
            '--theme-primary-light': '#c77dff',
            '--theme-secondary': '#7b2cbf',
            '--theme-secondary-light': '#9d4edd',

            // Accent colors
            '--theme-accent': '#f5c518',
            '--theme-accent-light': '#ffd700',
            '--theme-accent-dark': '#d4a516',

            // Text colors  
            '--theme-text': '#fef3c7',
            '--theme-text-secondary': '#d4c5a9',
            '--theme-text-muted': '#a78bfa',

            // Glass/card backgrounds
            '--theme-glass-bg': 'rgba(88, 28, 135, 0.15)',
            '--theme-glass-border': 'rgba(168, 85, 247, 0.25)',
            '--theme-card-glow': 'rgba(147, 51, 234, 0.3)',

            // Button colors
            '--theme-button-primary': 'linear-gradient(135deg, #f5c518, #ffd700, #f5c518)',
            '--theme-button-secondary-bg': 'rgba(147, 51, 234, 0.2)',
            '--theme-button-secondary-border': 'rgba(168, 85, 247, 0.4)',

            // Glows
            '--theme-glow': 'rgba(147, 51, 234, 0.3)',
            '--theme-accent-glow': 'rgba(245, 197, 24, 0.3)',

            // Nebula backgrounds
            '--nebula-1': 'rgba(147, 51, 234, 0.15)',
            '--nebula-2': 'rgba(192, 132, 252, 0.12)',
            '--nebula-3': 'rgba(168, 85, 247, 0.08)',

            // Status colors
            '--theme-success': '#10b981',
            '--theme-warning': '#f59e0b',
            '--theme-error': '#ef4444',
        }
    },
    celestial: {
        name: 'Celestial Blue',
        id: 'celestial',
        description: 'Ethereal blues with cyan accents',
        css: {
            // Primary palette
            '--theme-primary': '#3b82f6',
            '--theme-primary-light': '#60a5fa',
            '--theme-secondary': '#1d4ed8',
            '--theme-secondary-light': '#3b82f6',

            // Accent colors - cyan/teal
            '--theme-accent': '#22d3ee',
            '--theme-accent-light': '#67e8f9',
            '--theme-accent-dark': '#06b6d4',

            // Text colors
            '--theme-text': '#e0f2fe',
            '--theme-text-secondary': '#bae6fd',
            '--theme-text-muted': '#7dd3fc',

            // Glass/card backgrounds
            '--theme-glass-bg': 'rgba(30, 64, 175, 0.15)',
            '--theme-glass-border': 'rgba(59, 130, 246, 0.25)',
            '--theme-card-glow': 'rgba(59, 130, 246, 0.3)',

            // Button colors
            '--theme-button-primary': 'linear-gradient(135deg, #22d3ee, #67e8f9, #22d3ee)',
            '--theme-button-secondary-bg': 'rgba(59, 130, 246, 0.2)',
            '--theme-button-secondary-border': 'rgba(96, 165, 250, 0.4)',

            // Glows
            '--theme-glow': 'rgba(59, 130, 246, 0.3)',
            '--theme-accent-glow': 'rgba(34, 211, 238, 0.3)',

            // Nebula backgrounds
            '--nebula-1': 'rgba(59, 130, 246, 0.18)',
            '--nebula-2': 'rgba(6, 182, 212, 0.12)',
            '--nebula-3': 'rgba(99, 102, 241, 0.08)',

            // Status colors
            '--theme-success': '#34d399',
            '--theme-warning': '#fbbf24',
            '--theme-error': '#f87171',
        }
    },
    ember: {
        name: 'Ember Oracle',
        id: 'ember',
        description: 'Fiery reds and oranges with amber glow',
        css: {
            // Primary palette
            '--theme-primary': '#ef4444',
            '--theme-primary-light': '#f87171',
            '--theme-secondary': '#dc2626',
            '--theme-secondary-light': '#ef4444',

            // Accent colors - amber/gold
            '--theme-accent': '#fbbf24',
            '--theme-accent-light': '#fcd34d',
            '--theme-accent-dark': '#f59e0b',

            // Text colors
            '--theme-text': '#fef3c7',
            '--theme-text-secondary': '#fde68a',
            '--theme-text-muted': '#fca5a5',

            // Glass/card backgrounds
            '--theme-glass-bg': 'rgba(153, 27, 27, 0.15)',
            '--theme-glass-border': 'rgba(239, 68, 68, 0.25)',
            '--theme-card-glow': 'rgba(239, 68, 68, 0.3)',

            // Button colors
            '--theme-button-primary': 'linear-gradient(135deg, #fbbf24, #fcd34d, #fbbf24)',
            '--theme-button-secondary-bg': 'rgba(239, 68, 68, 0.2)',
            '--theme-button-secondary-border': 'rgba(248, 113, 113, 0.4)',

            // Glows
            '--theme-glow': 'rgba(239, 68, 68, 0.3)',
            '--theme-accent-glow': 'rgba(251, 191, 36, 0.3)',

            // Nebula backgrounds
            '--nebula-1': 'rgba(239, 68, 68, 0.18)',
            '--nebula-2': 'rgba(249, 115, 22, 0.12)',
            '--nebula-3': 'rgba(234, 179, 8, 0.08)',

            // Status colors
            '--theme-success': '#4ade80',
            '--theme-warning': '#fde047',
            '--theme-error': '#fb7185',
        }
    },
    mysticRose: {
        name: 'Mystic Rose',
        id: 'mysticRose',
        description: 'Enchanting pinks and magentas',
        css: {
            // Primary palette
            '--theme-primary': '#ec4899',
            '--theme-primary-light': '#f472b6',
            '--theme-secondary': '#db2777',
            '--theme-secondary-light': '#ec4899',

            // Accent colors - rose gold
            '--theme-accent': '#fda4af',
            '--theme-accent-light': '#fecdd3',
            '--theme-accent-dark': '#fb7185',

            // Text colors
            '--theme-text': '#fdf2f8',
            '--theme-text-secondary': '#fbcfe8',
            '--theme-text-muted': '#f9a8d4',

            // Glass/card backgrounds
            '--theme-glass-bg': 'rgba(157, 23, 77, 0.15)',
            '--theme-glass-border': 'rgba(236, 72, 153, 0.25)',
            '--theme-card-glow': 'rgba(236, 72, 153, 0.3)',

            // Button colors
            '--theme-button-primary': 'linear-gradient(135deg, #fda4af, #fecdd3, #fda4af)',
            '--theme-button-secondary-bg': 'rgba(236, 72, 153, 0.2)',
            '--theme-button-secondary-border': 'rgba(244, 114, 182, 0.4)',

            // Glows
            '--theme-glow': 'rgba(236, 72, 153, 0.3)',
            '--theme-accent-glow': 'rgba(253, 164, 175, 0.3)',

            // Nebula backgrounds
            '--nebula-1': 'rgba(236, 72, 153, 0.18)',
            '--nebula-2': 'rgba(168, 85, 247, 0.12)',
            '--nebula-3': 'rgba(244, 114, 182, 0.08)',

            // Status colors
            '--theme-success': '#86efac',
            '--theme-warning': '#fef08a',
            '--theme-error': '#fca5a5',
        }
    },
    midnight: {
        name: 'Midnight Void',
        id: 'midnight',
        description: 'Deep blacks with silver starlight',
        css: {
            // Primary palette - silver/gray
            '--theme-primary': '#94a3b8',
            '--theme-primary-light': '#cbd5e1',
            '--theme-secondary': '#64748b',
            '--theme-secondary-light': '#94a3b8',

            // Accent colors - silver/white
            '--theme-accent': '#e2e8f0',
            '--theme-accent-light': '#f1f5f9',
            '--theme-accent-dark': '#cbd5e1',

            // Text colors
            '--theme-text': '#f8fafc',
            '--theme-text-secondary': '#e2e8f0',
            '--theme-text-muted': '#94a3b8',

            // Glass/card backgrounds
            '--theme-glass-bg': 'rgba(30, 41, 59, 0.25)',
            '--theme-glass-border': 'rgba(148, 163, 184, 0.2)',
            '--theme-card-glow': 'rgba(148, 163, 184, 0.2)',

            // Button colors
            '--theme-button-primary': 'linear-gradient(135deg, #e2e8f0, #f1f5f9, #e2e8f0)',
            '--theme-button-secondary-bg': 'rgba(100, 116, 139, 0.2)',
            '--theme-button-secondary-border': 'rgba(148, 163, 184, 0.3)',

            // Glows
            '--theme-glow': 'rgba(148, 163, 184, 0.2)',
            '--theme-accent-glow': 'rgba(226, 232, 240, 0.2)',

            // Nebula backgrounds - very subtle
            '--nebula-1': 'rgba(100, 116, 139, 0.1)',
            '--nebula-2': 'rgba(71, 85, 105, 0.08)',
            '--nebula-3': 'rgba(51, 65, 85, 0.06)',

            // Status colors
            '--theme-success': '#4ade80',
            '--theme-warning': '#fbbf24',
            '--theme-error': '#f87171',
        }
    },
    emerald: {
        name: 'Emerald Forest',
        id: 'emerald',
        description: 'Mystical greens with nature essence',
        css: {
            // Primary palette
            '--theme-primary': '#10b981',
            '--theme-primary-light': '#34d399',
            '--theme-secondary': '#059669',
            '--theme-secondary-light': '#10b981',

            // Accent colors - gold/amber
            '--theme-accent': '#fbbf24',
            '--theme-accent-light': '#fcd34d',
            '--theme-accent-dark': '#f59e0b',

            // Text colors
            '--theme-text': '#ecfdf5',
            '--theme-text-secondary': '#a7f3d0',
            '--theme-text-muted': '#6ee7b7',

            // Glass/card backgrounds
            '--theme-glass-bg': 'rgba(6, 78, 59, 0.2)',
            '--theme-glass-border': 'rgba(16, 185, 129, 0.25)',
            '--theme-card-glow': 'rgba(16, 185, 129, 0.3)',

            // Button colors
            '--theme-button-primary': 'linear-gradient(135deg, #fbbf24, #fcd34d, #fbbf24)',
            '--theme-button-secondary-bg': 'rgba(16, 185, 129, 0.2)',
            '--theme-button-secondary-border': 'rgba(52, 211, 153, 0.4)',

            // Glows
            '--theme-glow': 'rgba(16, 185, 129, 0.3)',
            '--theme-accent-glow': 'rgba(251, 191, 36, 0.3)',

            // Nebula backgrounds
            '--nebula-1': 'rgba(16, 185, 129, 0.15)',
            '--nebula-2': 'rgba(52, 211, 153, 0.1)',
            '--nebula-3': 'rgba(6, 95, 70, 0.08)',

            // Status colors
            '--theme-success': '#34d399',
            '--theme-warning': '#fbbf24',
            '--theme-error': '#f87171',
        }
    }
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState('cosmic');

    // Load theme from localStorage
    useEffect(() => {
        const savedTheme = localStorage.getItem('cosmicTheme');
        if (savedTheme && THEMES[savedTheme]) {
            setCurrentTheme(savedTheme);
        }
    }, []);

    // Apply theme CSS variables
    useEffect(() => {
        const theme = THEMES[currentTheme];
        if (theme) {
            const root = document.documentElement;
            Object.entries(theme.css).forEach(([key, value]) => {
                root.style.setProperty(key, value);
            });
        }
        localStorage.setItem('cosmicTheme', currentTheme);
    }, [currentTheme]);

    const setTheme = (themeId) => {
        if (THEMES[themeId]) {
            setCurrentTheme(themeId);
        }
    };

    const value = {
        currentTheme,
        setTheme,
        theme: THEMES[currentTheme],
        themes: THEMES
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext;
